import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  Lock,
  Loader2,
  ShieldCheck,
  CalendarClock,
  Download,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

/**
 * In-app viewer for `public/documents/company-profile-manifest.json`.
 * Surfaces the latest profile version + a per-group breakdown of
 * declared compliance documents (status, expiry, file availability).
 *
 * The manifest itself is regenerated at dev/build time by
 * `scripts/company-profile-manifest-plugin.ts`.
 */

type Availability = 'available' | 'on_request' | 'missing';
type EffectiveStatus = 'valid' | 'inProgress' | 'expired';

interface ManifestFileMeta {
  url: string;
  exists: boolean;
  bytes?: number;
  modifiedAt?: string;
}

interface ManifestComplianceItem {
  labelKey: string;
  value: string;
  isCode: boolean;
  validUntil: string | null;
  declaredStatus: EffectiveStatus | null;
  effectiveStatus: EffectiveStatus;
  file: ManifestFileMeta | null;
  availability: Availability;
}

interface ManifestComplianceGroup {
  id: string;
  titleKey: string;
  counts: {
    total: number;
    available: number;
    onRequest: number;
    missing: number;
    expired: number;
    inProgress: number;
  };
  items: ManifestComplianceItem[];
}

interface ManifestCertification {
  id: string;
  nameKey: string;
  type: 'legal' | 'sector' | 'insurance' | 'qhse';
  typeKey: string;
  reference: string;
  isReferenceTranslationKey: boolean;
  validUntil: string | null;
  status: EffectiveStatus;
  declaredStatus: EffectiveStatus | null;
  file: ManifestFileMeta | null;
  availability: Availability;
}

interface Manifest {
  generatedAt: string;
  profile: {
    currentVersion: { version: string; releaseDate: string } | null;
    pdfs: {
      fr: ManifestFileMeta & { url: string };
      en: ManifestFileMeta & { url: string };
    };
    archives: Array<{
      version: string;
      releaseDate: string;
      fr: ManifestFileMeta;
      en: ManifestFileMeta;
    }>;
  };
  compliance: {
    directory: string;
    groups: ManifestComplianceGroup[];
    summary: ManifestComplianceGroup['counts'];
  };
  certifications?: {
    directory: string;
    total: number;
    items: ManifestCertification[];
  };
}

const MANIFEST_URL = '/documents/company-profile-manifest.json';

const formatBytes = (bytes?: number): string => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const ProfileManifestViewer = () => {
  const { t, language } = useLanguage();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [tab, setTab] = useState<'overview' | 'certifications'>('overview');
  const [certTypeFilter, setCertTypeFilter] = useState<'all' | ManifestCertification['type']>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${MANIFEST_URL}?t=${Date.now()}`, { cache: 'no-store' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as Manifest;
        if (!cancelled) setManifest(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'load_error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fmtDate = (iso?: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const summary = manifest?.compliance.summary;

  const totalIssues = useMemo(() => {
    if (!summary) return 0;
    return summary.missing + summary.expired;
  }, [summary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('manifest.loading')}
      </div>
    );
  }

  if (error || !manifest) {
    return (
      <div className="p-6 text-sm text-destructive flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        {t('manifest.error')}
      </div>
    );
  }

  const renderStatusBadge = (status: EffectiveStatus) => {
    const map: Record<
      EffectiveStatus,
      { Icon: typeof CheckCircle2; classes: string; key: string }
    > = {
      valid: {
        Icon: CheckCircle2,
        classes: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
        key: 'certs.status.valid',
      },
      inProgress: {
        Icon: Clock,
        classes: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
        key: 'certs.status.inProgress',
      },
      expired: {
        Icon: AlertTriangle,
        classes: 'bg-destructive/10 text-destructive border-destructive/20',
        key: 'certs.status.expired',
      },
    };
    const { Icon, classes, key } = map[status];
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold',
          classes,
        )}
      >
        <Icon className="w-2.5 h-2.5" aria-hidden="true" />
        {t(key)}
      </span>
    );
  };

  const renderAvailabilityBadge = (a: Availability) => {
    if (a === 'available') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">
          <Download className="w-2.5 h-2.5" /> {t('manifest.avail.available')}
        </span>
      );
    }
    if (a === 'on_request') {
      return (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold bg-secondary text-muted-foreground border-border">
          <Lock className="w-2.5 h-2.5" /> {t('manifest.avail.onRequest')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-semibold bg-destructive/10 text-destructive border-destructive/20">
        <AlertTriangle className="w-2.5 h-2.5" /> {t('manifest.avail.missing')}
      </span>
    );
  };

  // Reference resolution: translation key vs literal value
  const resolveReference = (cert: ManifestCertification) =>
    cert.isReferenceTranslationKey ? t(cert.reference) : cert.reference;

  const certifications = manifest.certifications?.items ?? [];
  const filteredCerts =
    certTypeFilter === 'all'
      ? certifications
      : certifications.filter((c) => c.type === certTypeFilter);

  const certTypes: Array<'all' | ManifestCertification['type']> = [
    'all',
    'legal',
    'sector',
    'insurance',
    'qhse',
  ];

  return (
    <div className="space-y-4 p-4 text-sm">
      {/* Tabs: Overview vs Certifications (mapped from manifest) */}
      <div
        role="tablist"
        aria-label={t('manifest.tabs.aria')}
        className="inline-flex rounded-md border border-border overflow-hidden text-xs"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'overview'}
          onClick={() => setTab('overview')}
          className={cn(
            'px-3 py-1.5 font-semibold transition-colors',
            tab === 'overview'
              ? 'bg-accent text-accent-foreground'
              : 'bg-background text-muted-foreground hover:bg-secondary',
          )}
        >
          {t('manifest.tabs.overview')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'certifications'}
          onClick={() => setTab('certifications')}
          className={cn(
            'px-3 py-1.5 font-semibold transition-colors border-l border-border',
            tab === 'certifications'
              ? 'bg-accent text-accent-foreground'
              : 'bg-background text-muted-foreground hover:bg-secondary',
          )}
        >
          {t('manifest.tabs.certs')}{' '}
          <span className="opacity-70">({certifications.length})</span>
        </button>
      </div>

      {tab === 'certifications' && (
        <div className="space-y-3">
          {/* Type filter chips */}
          <div className="flex flex-wrap gap-1">
            {certTypes.map((type) => {
              const isActive = certTypeFilter === type;
              const count =
                type === 'all'
                  ? certifications.length
                  : certifications.filter((c) => c.type === type).length;
              const label =
                type === 'all'
                  ? t('manifest.certs.filter.all')
                  : t(`certs.${type}`);
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setCertTypeFilter(type)}
                  aria-pressed={isActive}
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors',
                    isActive
                      ? 'bg-accent text-accent-foreground border-accent'
                      : 'bg-background text-muted-foreground border-border hover:bg-secondary',
                  )}
                >
                  {label}
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-[10px] font-bold',
                      isActive ? 'bg-accent-foreground/20' : 'bg-muted',
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Mapped table — name / type / status / date / file */}
          <div className="rounded-lg border border-border overflow-hidden bg-card">
            {filteredCerts.length === 0 ? (
              <div className="p-4 text-xs text-muted-foreground text-center">
                {t('manifest.certs.empty')}
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filteredCerts.map((cert) => (
                  <li key={cert.id} className="px-3 py-2.5 space-y-1.5">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-foreground text-xs leading-snug">
                          {t(cert.nameKey)}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-accent font-semibold">
                            {t(cert.typeKey)}
                          </span>
                          <span className="text-[10px] text-muted-foreground">·</span>
                          <span className="text-[10px] text-muted-foreground font-mono truncate">
                            {resolveReference(cert)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {renderStatusBadge(cert.status)}
                        {renderAvailabilityBadge(cert.availability)}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" aria-hidden="true" />
                        {cert.validUntil
                          ? `${t('certs.validUntil')} ${fmtDate(cert.validUntil)}`
                          : t('manifest.certs.noExpiry')}
                      </span>
                      {cert.file ? (
                        cert.file.exists ? (
                          <a
                            href={cert.file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-accent hover:underline font-mono"
                          >
                            <Download className="w-3 h-3" aria-hidden="true" />
                            {cert.file.url.split('/').pop()}
                            <span className="text-muted-foreground">
                              · {formatBytes(cert.file.bytes)}
                            </span>
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-destructive font-mono">
                            <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                            {cert.file.url.split('/').pop()} ·{' '}
                            {t('manifest.avail.missing')}
                          </span>
                        )
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Lock className="w-3 h-3" aria-hidden="true" />
                          {t('manifest.avail.onRequest')}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'overview' && (
        <>
      {/* Header summary */}
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-accent" aria-hidden="true" />
          <h3 className="font-heading font-bold text-foreground">
            {t('manifest.profile.title')}
          </h3>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <dt className="text-muted-foreground">{t('manifest.version')}</dt>
          <dd className="font-mono">
            v{manifest.profile.currentVersion?.version ?? '—'} ·{' '}
            {fmtDate(manifest.profile.currentVersion?.releaseDate)}
          </dd>
          <dt className="text-muted-foreground">{t('manifest.generatedAt')}</dt>
          <dd className="font-mono">{fmtDate(manifest.generatedAt)}</dd>
          <dt className="text-muted-foreground">PDF FR</dt>
          <dd>
            {manifest.profile.pdfs.fr.exists ? (
              <span className="text-emerald-700 dark:text-emerald-400">
                ✓ {formatBytes(manifest.profile.pdfs.fr.bytes)}
              </span>
            ) : (
              <span className="text-destructive">✗ {t('manifest.avail.missing')}</span>
            )}
          </dd>
          <dt className="text-muted-foreground">PDF EN</dt>
          <dd>
            {manifest.profile.pdfs.en.exists ? (
              <span className="text-emerald-700 dark:text-emerald-400">
                ✓ {formatBytes(manifest.profile.pdfs.en.bytes)}
              </span>
            ) : (
              <span className="text-destructive">✗ {t('manifest.avail.missing')}</span>
            )}
          </dd>
        </dl>
      </div>

      {/* Compliance summary */}
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-accent" aria-hidden="true" />
            <h3 className="font-heading font-bold text-foreground">
              {t('manifest.compliance.title')}
            </h3>
          </div>
          {totalIssues > 0 ? (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-destructive/10 text-destructive border border-destructive/20">
              {totalIssues} {t('manifest.issues')}
            </span>
          ) : (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
              ✓ {t('manifest.allOk')}
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded border border-border bg-background p-2">
            <div className="font-bold text-foreground">{summary?.available ?? 0}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {t('manifest.avail.available')}
            </div>
          </div>
          <div className="rounded border border-border bg-background p-2">
            <div className="font-bold text-foreground">{summary?.onRequest ?? 0}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {t('manifest.avail.onRequest')}
            </div>
          </div>
          <div className="rounded border border-border bg-background p-2">
            <div className="font-bold text-foreground">{summary?.missing ?? 0}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {t('manifest.avail.missing')}
            </div>
          </div>
        </div>
      </div>

      {/* Per-group details */}
      <div className="space-y-2">
        {manifest.compliance.groups.map((group) => {
          const open = openGroups[group.id] ?? false;
          const hasIssue = group.counts.missing + group.counts.expired > 0;
          return (
            <div
              key={group.id}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={open}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-secondary/50 transition-colors"
              >
                <span className="flex items-center gap-2 font-semibold text-foreground text-xs">
                  {t(group.titleKey)}
                  <span className="text-muted-foreground font-normal">
                    ({group.counts.total})
                  </span>
                  {hasIssue && (
                    <AlertTriangle className="w-3 h-3 text-destructive" aria-hidden="true" />
                  )}
                </span>
                {open ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
              {open && (
                <ul className="divide-y divide-border border-t border-border">
                  {group.items.map((item, i) => (
                    <li key={i} className="px-3 py-2 space-y-1">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-xs">
                          {t(item.labelKey)}
                        </span>
                        <div className="flex items-center gap-1 flex-wrap">
                          {renderStatusBadge(item.effectiveStatus)}
                          {renderAvailabilityBadge(item.availability)}
                        </div>
                      </div>
                      {item.validUntil && (
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <CalendarClock className="w-3 h-3" />
                          {t('certs.validUntil')} {fmtDate(item.validUntil)}
                        </div>
                      )}
                      {item.file && (
                        <div className="text-[11px] font-mono text-muted-foreground truncate">
                          {item.file.url}
                          {item.file.exists ? (
                            <span className="ml-1 text-emerald-700 dark:text-emerald-400">
                              · {formatBytes(item.file.bytes)}
                            </span>
                          ) : (
                            <span className="ml-1 text-destructive">· {t('manifest.avail.missing')}</span>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-muted-foreground text-center pt-2">
        <a
          href={MANIFEST_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-accent"
        >
          {t('manifest.viewRaw')}
        </a>
      </p>
        </>
      )}
    </div>
  );
};

export default ProfileManifestViewer;
